import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowLeft, RefreshCw, CheckCircle, XCircle, Loader2 } from "lucide-react";
import AxonMetricHeader from "../../components/layout/AxonMetricHeader.jsx";
import backendApi from "../../services/backendApi";

const OTPVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const otpInputRefs = React.useRef([]);

  // Get email from location state or query params
  useEffect(() => {
    const emailFromState = location.state?.email || new URLSearchParams(location.search).get('email');
    if (emailFromState) {
      setEmail(emailFromState);
    } else {
      // If no email, redirect to signup
      navigate('/signup');
    }
  }, [location, navigate]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    setSuccess('');

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6);
        const newOtp = [...otp];
        digits.split('').forEach((digit, i) => {
          if (index + i < 6) {
            newOtp[index + i] = digit;
          }
        });
        setOtp(newOtp);
        setError('');
        // Focus the next empty input or last input
        const nextIndex = Math.min(index + digits.length, 5);
        otpInputRefs.current[nextIndex]?.focus();
      });
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    if (!email) {
      setError('Email is missing. Please start over.');
      navigate('/signup');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await backendApi.verifyOTP(email, otpCode);
      
      if (response.success) {
        setSuccess('Email verified successfully! Creating your account...');
        setTimeout(() => {
          navigate('/login', { state: { message: 'Account created successfully! Please login.' } });
        }, 2000);
      } else {
        if (response.errorCode === 'OTP_EXPIRED') {
          setError('Verification code has expired. Please request a new one.');
        } else if (response.errorCode === 'MAX_ATTEMPTS_EXCEEDED') {
          setError('Too many attempts. Please request a new verification code.');
        } else {
          setError(response.error || 'Invalid verification code. Please try again.');
          // Clear OTP on error
          setOtp(['', '', '', '', '', '']);
          if (otpInputRefs.current[0]) {
            otpInputRefs.current[0].focus();
          }
        }
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      console.error('OTP verification error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Email is missing. Please start over.');
      navigate('/signup');
      return;
    }
    
    if (countdown > 0) {
      return; // Don't allow resend during countdown
    }
    
    setIsResending(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await backendApi.resendOTP(email);
      
      if (response.success) {
        setSuccess('A new verification code has been sent to your email.');
        setCountdown(60); // 60 second countdown
        // Clear OTP inputs
        setOtp(['', '', '', '', '', '']);
        if (otpInputRefs.current[0]) {
          otpInputRefs.current[0].focus();
        }
      } else {
        setError(response.error || 'Failed to resend code. Please try again.');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      console.error('Resend OTP error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const maskedEmail = email ? email.replace(/(.{2})(.*)(@.*)/, '$1****$3') : '';

  return (
    <>
      {/* Enhanced CSS Styles with Professional Gradients */}
      <style>{`
        .glass-morphism {
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .brand-glass {
          background: linear-gradient(135deg, 
            rgba(255, 255, 255, 0.25) 0%, 
            rgba(255, 255, 255, 0.15) 50%, 
            rgba(0, 132, 207, 0.1) 100%
          );
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }
        
        .floating-animation {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .gradient-border {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 25%, 
            #1C4D88 50%, 
            #0084CF 75%, 
            #0084CF 100%
          );
          background-size: 300% 300%;
          animation: gradientShift 8s ease infinite;
          padding: 2px;
          border-radius: 20px;
          backdrop-filter: blur(20px);
          box-shadow: 0 25px 50px -12px rgba(0, 132, 207, 0.25);
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .inner-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(30px);
          border-radius: 18px;
        }

        .background-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-image: url('/assets/login_bg.jpg');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          z-index: -2;
        }

        .background-overlay {
          display: none;
        }
        
        @keyframes overlayShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .otp-input {
          width: 60px;
          height: 60px;
          text-align: center;
          font-size: 24px;
          font-weight: bold;
          border: 2px solid rgba(0, 132, 207, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
        }

        .otp-input:focus {
          outline: none;
          border-color: #0084CF;
          box-shadow: 0 0 0 3px rgba(0, 132, 207, 0.1);
          transform: scale(1.05);
        }

        .professional-button {
          background: linear-gradient(135deg, 
            #0084CF 0%, 
            #0084CF 50%, 
            #1C4D88 100%
          );
          transition: all 0.3s ease;
        }

        .professional-button:hover {
          background: linear-gradient(135deg, 
            #0066A3 0%, 
            #0066A3 50%, 
            #68d391 100%
          );
          transform: translateY(-2px);
          box-shadow: 0 15px 35px rgba(0, 132, 207, 0.4);
        }
      `}</style>

      {/* Keep Original Background Image */}
      <div className="background-container"></div>
      <div className="background-overlay"></div>

      <div className="min-h-screen w-full flex flex-col relative">
        {/* Header Section */}
        <AxonMetricHeader />

        {/* Main Content */}
        <main className="relative z-10 flex-1 w-full flex items-center justify-center px-4 py-6" style={{minHeight: 'calc(100vh - 50px)', paddingTop: '80px'}}>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full max-w-2xl mx-auto"
          >
            <div className="gradient-border">
              <div className="inner-content rounded-2xl overflow-hidden shadow-2xl p-8">
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="text-center mb-6"
                >
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-gradient-to-br from-[#0084CF]/20 via-[#C2DFF1]/40 to-[#C2DFF1]/30 rounded-full">
                      <Mail size={40} className="text-[#0084CF]" />
                    </div>
                  </div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent mb-2">
                    Verify Your Email
                  </h1>
                  <p className="text-sm text-gray-700 font-medium">
                    We've sent a verification code to
                  </p>
                  <p className="text-sm font-bold text-[#0084CF] mt-1">
                    {maskedEmail}
                  </p>
                </motion.div>

                <form onSubmit={handleVerify} className="space-y-6">
                  {/* OTP Input Fields */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="flex justify-center gap-3"
                  >
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="otp-input"
                        disabled={isLoading}
                      />
                    ))}
                  </motion.div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-red-50/95 backdrop-blur-sm border border-red-200 rounded-xl p-4"
                    >
                      <p className="text-sm text-red-700 font-semibold text-center flex items-center justify-center gap-2">
                        <XCircle size={18} />
                        {error}
                      </p>
                    </motion.div>
                  )}

                  {success && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-green-50/95 backdrop-blur-sm border border-green-200 rounded-xl p-4"
                    >
                      <p className="text-sm text-green-700 font-semibold text-center flex items-center justify-center gap-2">
                        <CheckCircle size={18} />
                        {success}
                      </p>
                    </motion.div>
                  )}

                  {/* Resend OTP */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="text-center"
                  >
                    <p className="text-sm text-gray-600 mb-2">
                      Didn't receive the code?
                    </p>
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={isResending || countdown > 0 || isLoading}
                      className={`inline-flex items-center gap-2 text-sm font-semibold text-[#0084CF] hover:text-[#0066A3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        countdown > 0 ? 'cursor-not-allowed' : ''
                      }`}
                    >
                      {isResending ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Sending...
                        </>
                      ) : countdown > 0 ? (
                        <>
                          <RefreshCw size={16} />
                          Resend code in {countdown}s
                        </>
                      ) : (
                        <>
                          <RefreshCw size={16} />
                          Resend Verification Code
                        </>
                      )}
                    </button>
                  </motion.div>

                  {/* Verify Button */}
                  <motion.button
                    type="submit"
                    disabled={isLoading || otp.join('').length !== 6}
                    whileHover={{ scale: isLoading ? 1 : 1.02 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98 }}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className={`w-full py-3 mt-6 rounded-xl font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center shadow-xl ${
                      isLoading || otp.join('').length !== 6
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                        : "professional-button text-white shadow-[#0084CF]/40 hover:shadow-[#0084CF]/60 hover:shadow-2xl"
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-4">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Verifying...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Lock size={18} />
                        <span>Verify Email</span>
                      </div>
                    )}
                  </motion.button>

                  {/* Back to Signup */}
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="text-center mt-6"
                  >
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#0084CF] transition-colors"
                      disabled={isLoading}
                    >
                      <ArrowLeft size={16} />
                      Back to Sign Up
                    </button>
                  </motion.div>
                </form>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    </>
  );
};

export default OTPVerificationPage;
